import { useState, useEffect, Component } from "react";
import {
    Button,
    Card,
    Space,
    Typography,
    Collapse,
    Alert,
    Divider,
    Tag,
    Modal,
    Steps,
    Tooltip
} from "antd";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
    HomeOutlined,
    ReloadOutlined,
    CopyOutlined,
    BugOutlined,
    QuestionCircleOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    MailOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import { minimalToast } from "../utils/toast";
import { NotificationSeverity } from "../utils/toast";
import { NotificationPriority } from "../utils/toast";
import { NotificationCategory } from "../utils/toast";
import { useAppDispatch } from "../hooks/hooks";
import {
    isIgnoredBrowserNoise,
} from "../utils/sentryNoise";
import {
    formatDisplayError,
    isPublicAssessmentPath,
} from "../utils/formatDisplayError";
import { isChunkLoadError, recoverFromChunkError } from "../utils/chunkReload";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Step } = Steps;

// Function component for error display
const ErrorDisplay = ({ hasError, errorInfo, setHasError }: { hasError: boolean, errorInfo: any, setHasError: (hasError: boolean) => void }) => {
    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState(false);
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [copiedDetails, setCopiedDetails] = useState(false);
    const dispatch = useAppDispatch();
    const publicAssessment = isPublicAssessmentPath();
    const getErrorMessage = () =>
        formatDisplayError(errorInfo, { publicAssessment });

    const formatStackTrace = () => {
        if (!errorInfo?.stacktrace?.sections) return "No stack trace available";

        return errorInfo.stacktrace.sections
            .map(section =>
                section.frames
                    ?.map(frame => `at ${frame.function} (${frame.filename}:${frame.lineno}:${frame.colno})`)
                    .join('\n') || ''
            )
            .join('\n\n');
    };

    const copyErrorDetails = () => {
        const timestamp = new Date().toISOString();
        const userAgent = navigator.userAgent;
        const currentUrl = window.location.href;

        const details = `
=== Error Report ===
Timestamp: ${timestamp}
URL: ${currentUrl}
User Agent: ${userAgent}

Error Message: ${getErrorMessage()}

Stack Trace:
${formatStackTrace()}

=== End of Report ===
        `.trim();

        navigator.clipboard.writeText(details)
            .then(() => {
                minimalToast({
                    dispatch: dispatch,
                    title: "Success",
                    message: "Error details copied to clipboard!",
                    type: NotificationSeverity.SUCCESS,
                    category: NotificationCategory.ALERT,
                    priority: NotificationPriority.HIGH,
                });
                setCopiedDetails(true);
                setTimeout(() => setCopiedDetails(false), 3000);
            })
            .catch(() => {
                minimalToast({
                    dispatch: dispatch,
                    title: "Error",
                    message: "Failed to copy error details",
                    type: NotificationSeverity.ERROR,
                    category: NotificationCategory.ALERT,
                    priority: NotificationPriority.HIGH,
                });
            });
    };

    const handleReload = () => {
        setHasError(false);
        window.location.reload();
    };

    const handleGoHome = () => {
        setHasError(false);
        navigate("/");
    };

    const troubleshootingSteps = [
        {
            title: "Refresh the page",
            description: "Sometimes a simple refresh can resolve temporary issues",
            icon: <ReloadOutlined />,
            action: handleReload,
        },
        {
            title: "Clear browser cache",
            description: "Clear your browser's cache and cookies for this site",
            icon: <InfoCircleOutlined />,
        },
        {
            title: "Check internet connection",
            description: "Ensure you have a stable internet connection",
            icon: <WarningOutlined />,
        },
        {
            title: "Try a different browser",
            description: "The issue might be browser-specific",
            icon: <CheckCircleOutlined />,
        },
    ];

    if (hasError) {
        if (publicAssessment) {
            return (
                <div style={{
                    minHeight: "100vh",
                    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 20,
                }}>
                    <Card style={{ maxWidth: 520, width: "100%", borderRadius: 16 }}>
                        <Alert
                            type="warning"
                            showIcon
                            message="Could not open assessment"
                            description={getErrorMessage()}
                            style={{ marginBottom: 16 }}
                        />
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<ReloadOutlined />}
                                block
                                onClick={handleReload}
                            >
                                Refresh and try again
                            </Button>
                            <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
                                Tip: If you are using your phone&apos;s built-in browser, try opening
                                this link in Chrome for the best experience.
                            </Paragraph>
                        </Space>
                    </Card>
                </div>
            );
        }

        return (
            <div style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
            }}>
                <Card
                    style={{
                        maxWidth: 800,
                        width: "100%",
                        borderRadius: 16,
                        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    {/* Header Section */}
                    <div style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        padding: "40px 30px",
                        textAlign: "center",
                        borderRadius: "16px 16px 0 0",
                    }}>
                        <BugOutlined style={{ fontSize: 64, color: "white", marginBottom: 20 }} />
                        <Title level={2} style={{ color: "white", margin: 0 }}>
                            Oops! Something went wrong
                        </Title>
                        <Paragraph style={{ color: "rgba(255,255,255,0.9)", fontSize: 16, marginTop: 10 }}>
                            Don't worry, we're here to help you get back on track
                        </Paragraph>
                    </div>

                    {/* Main Content */}
                    <div style={{ padding: "30px" }}>
                        {/* Error Message Alert */}
                        <Alert
                            message="Error Details"
                            description={getErrorMessage()}
                            type="error"
                            showIcon
                            style={{ marginBottom: 24 }}
                            action={
                                <Tooltip title={copiedDetails ? "Copied!" : "Copy error details"}>
                                    <Button
                                        size="small"
                                        icon={copiedDetails ? <CheckCircleOutlined /> : <CopyOutlined />}
                                        onClick={copyErrorDetails}
                                    >
                                        {copiedDetails ? "Copied" : "Copy"}
                                    </Button>
                                </Tooltip>
                            }
                        />

                        {/* Quick Actions */}
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5} style={{ marginBottom: 16 }}>
                                Quick Actions
                            </Title>
                            <Space size="middle" wrap>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReload}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    size="large"
                                    icon={<HomeOutlined />}
                                    onClick={handleGoHome}
                                >
                                    Go to Home
                                </Button>
                                <Button
                                    size="large"
                                    icon={<MailOutlined />}
                                    onClick={() => setReportModalVisible(true)}
                                >
                                    Report Issue
                                </Button>
                            </Space>
                        </div>

                        <Divider />

                        {/* Troubleshooting Steps */}
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5} style={{ marginBottom: 16 }}>
                                <QuestionCircleOutlined style={{ marginRight: 8 }} />
                                Troubleshooting Steps
                            </Title>
                            <Steps direction="vertical" size="small">
                                {troubleshootingSteps.map((step, index) => (
                                    <Step
                                        key={index}
                                        title={step.title}
                                        description={
                                            <Space direction="vertical" style={{ width: "100%" }}>
                                                <Text type="secondary">{step.description}</Text>
                                                {step.action && (
                                                    <Button size="small" onClick={step.action}>
                                                        Try this
                                                    </Button>
                                                )}
                                            </Space>
                                        }
                                        icon={step.icon}
                                        status={index === 0 ? "process" : "wait"}
                                    />
                                ))}
                            </Steps>
                        </div>

                        {/* Technical Details Collapse */}
                        <Collapse
                            ghost
                            onChange={(key) => setShowDetails(key.includes("1"))}
                        >
                            <Panel
                                header={
                                    <Space>
                                        <FileTextOutlined />
                                        <Text strong>Technical Details</Text>
                                        <Tag color="orange">For Developers</Tag>
                                    </Space>
                                }
                                key="1"
                            >
                                <Alert
                                    message="Stack Trace"
                                    type="info"
                                    style={{ marginBottom: 16 }}
                                />
                                <Card
                                    size="small"
                                    style={{
                                        background: "#f5f5f5",
                                        borderRadius: 8,
                                    }}
                                >
                                    <pre style={{
                                        margin: 0,
                                        fontSize: 12,
                                        overflowX: "auto",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}>
                                        {formatStackTrace()}
                                        {errorInfo?.stacktrace?.sections?.map((section) => (
                                            <div key={section.id}>
                                                <h3>{section.name}</h3>
                                                <pre>{section.frames?.map((frame) => frame.filename).join("\n")}</pre>
                                            </div>
                                        ))}
                                    </pre>
                                </Card>
                                <div style={{ marginTop: 16 }}>
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={copyErrorDetails}
                                        block
                                    >
                                        Copy Full Error Report
                                    </Button>
                                </div>
                            </Panel>
                        </Collapse>
                    </div>
                </Card>

                {/* Report Issue Modal */}
                <Modal
                    title={
                        <Space>
                            <MailOutlined />
                            <span>Report Issue</span>
                        </Space>
                    }
                    open={reportModalVisible}
                    onCancel={() => setReportModalVisible(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setReportModalVisible(false)}>
                            Cancel
                        </Button>,
                        <Button
                            key="email"
                            type="primary"
                            icon={<MailOutlined />}
                            onClick={() => {
                                const subject = encodeURIComponent("Error Report - Application Issue");
                                const body = encodeURIComponent(`
                                    Error Message: ${getErrorMessage()}

                                    Steps to reproduce:
                                    1. 
                                    2. 
                                    3. 

                                    Additional information:
                                    - Browser: ${navigator.userAgent}
                                    - Time: ${new Date().toISOString()}
                                    - URL: ${window.location.href}

                                    Stack Trace:
                                    ${formatStackTrace()}
                                `.trim());
                                window.location.href = `mailto:order@hypothesize.tech?subject=${subject}&body=${body}`;
                                setReportModalVisible(false);
                            }}
                        >
                            Send Email Report
                        </Button>,
                    ]}
                >
                    <Alert
                        message="Help us improve"
                        description="Your feedback helps us identify and fix issues faster. The error details will be automatically included in your report."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Paragraph>
                        Click "Send Email Report" to open your email client with pre-filled error information.
                        You can add additional details about what you were doing when the error occurred.
                    </Paragraph>
                </Modal>
            </div>
        );
    }

    return null;
};

// Class component wrapper for React Error Boundary
class ErrorBoundaryClass extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        if (isIgnoredBrowserNoise(error)) {
            return { hasError: false, errorInfo: null };
        }
        // A lazy route whose chunk no longer exists (tab was open across a
        // deploy) is not a real application error — reload onto the current
        // build instead of dead-ending the student. Keep the Suspense loader on
        // screen while that happens. If the reload budget is spent, fall
        // through and show the error UI.
        if (isChunkLoadError(error) && recoverFromChunkError()) {
            return { hasError: false, errorInfo: null };
        }
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        if (isIgnoredBrowserNoise(error)) return;
        // Already handled by the reload above; reporting it would just add
        // noise for every deploy.
        if (isChunkLoadError(error) && !(this.state as any).hasError) return;

        console.error("Error caught by boundary:", error, errorInfo);
        this.setState({ errorInfo: error });
        Sentry.captureException(error);
    }

    render() {
        if ((this.state as any).hasError) {
            return (
                <ErrorDisplay
                    hasError={(this.state as any).hasError}
                    errorInfo={(this.state as any).errorInfo}
                    setHasError={(hasError) => this.setState({ hasError })}
                />
            );
        }

        return (this.props as any).children;
    }
}

// Main ErrorBoundary component
const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    const [hasError, setHasError] = useState(false);
    const [errorInfo, setErrorInfo] = useState(null);

    useEffect(() => {
        const errorHandler = (event: ErrorEvent) => {
            if (
                isIgnoredBrowserNoise(
                    event.error,
                    event.message || event.filename
                )
            ) {
                event.preventDefault();
                return;
            }

            console.error("Uncaught error:", event.error);
            setHasError(true);
            setErrorInfo(event.error);
            Sentry.captureException(event.error ?? event.message);
        };

        window.addEventListener("error", errorHandler);

        const rejectionHandler = (event: PromiseRejectionEvent) => {
            if (isIgnoredBrowserNoise(event.reason, event.type)) {
                event.preventDefault();
                return;
            }
            // API and form flows handle their own failures; avoid a full-page crash
            // for stray promise rejections on public assessment entry URLs.
            if (isPublicAssessmentPath()) {
                console.error("Unhandled promise rejection on assessment page:", event.reason);
                event.preventDefault();
            }
        };

        window.addEventListener("unhandledrejection", rejectionHandler);

        return () => {
            window.removeEventListener("error", errorHandler);
            window.removeEventListener("unhandledrejection", rejectionHandler);
        };
    }, []);

    // Show error display for global errors
    if (hasError) {
        return <ErrorDisplay hasError={hasError} errorInfo={errorInfo} setHasError={setHasError} />;
    }

    // Wrap children in class-based error boundary for React errors
    return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
};

export default ErrorBoundary;